import { LoadingOutlined } from '@ant-design/icons';
import { Col, Row, Select, Spin } from 'antd';
import { isEmpty } from 'underscore';
import { DownloadType } from '../../../../../../types/metadata';
import { DownloadCheckboxKey } from '../../../../../../utils/common';
import PriceFormatter from '../../../../../../utils/euroPriceFormatter';
import styles from '../../../Style/DatasetInfo.module.scss';
import {
  DatasetActionButtons,
  DownloadButton,
} from '../../DatasetActionButtons';

interface IPriceSectionProps {
  hasDatasets: boolean;
  price: number;
  isTnw: boolean;
  namePanel: string;
  disableDownload: boolean;
  isLocationAvailable: boolean;
  isDTU: boolean;
  timeStepInterval: string;
  setTimeStepInterval: (value: string) => void;
  datasetId: string;
  addedToCart: boolean;
  showDownloadButton: boolean;
  showCancelDownloadButtonParameters: boolean;
  showCancelDownloadButton2DSpectre: boolean;
  show2DSpectra: boolean;
  isAuthenticated: boolean;
  userId?: string;
  userGroupPermissions: any;
  handleAddToCart: () => void;
  handleRemoveFromCart: () => void;
  handleStartDownloadProcess: (type: DownloadType) => void;
  handleCancelDownload: (type: DownloadType) => void;
  setShowSpectraModal: (value: boolean) => void;
  cartLoading: boolean;
  priceLoading: boolean;
  priceParameterSelection: string;
  priceSpectraSelection: string;
  setPriceParameterSelection: (item: string) => void;
  setPriceSpectraSelection: (item: string) => void;
  label?: string;
}

type TRenderActionButtonsParams = {
  showDownloadButton: boolean;
  showCancelButton: boolean;
  downloadType: DownloadType;
  disabled?: boolean;
  authenticatedCheck?: boolean;
  selectedAction: string;
  setSelectedAction: (value: string) => void;
};

export const PriceSection = ({
  hasDatasets,
  price,
  isTnw,
  namePanel,
  disableDownload,
  isLocationAvailable,
  isDTU,
  timeStepInterval,
  setTimeStepInterval,
  datasetId,
  addedToCart,
  showDownloadButton,
  showCancelDownloadButtonParameters,
  showCancelDownloadButton2DSpectre,
  show2DSpectra,
  isAuthenticated,
  userId,
  userGroupPermissions,
  handleAddToCart,
  handleRemoveFromCart,
  handleStartDownloadProcess,
  handleCancelDownload,
  setShowSpectraModal,
  cartLoading,
  label,
  priceLoading,
  priceParameterSelection,
  setPriceParameterSelection,
  priceSpectraSelection,
  setPriceSpectraSelection,
}: IPriceSectionProps) => {
  const renderActionButtons = ({
    showDownloadButton,
    showCancelButton,
    downloadType,
    selectedAction,
    setSelectedAction,
    authenticatedCheck = false,
    disabled = false,
  }: TRenderActionButtonsParams) => {
    const shouldShowDropdownForDownloadButton =
      downloadType === '2DSpectra' ? false : showDownloadButton;

    const startProcess = () => {
      if (authenticatedCheck && !isAuthenticated) {
        setShowSpectraModal(true);
        return;
      }
      handleStartDownloadProcess(downloadType);
    };

    if (showDownloadButton) {
      return selectedAction === 'download' ? (
        <DownloadButton
          id={`${label}_${namePanel}`}
          data-testid={`${label}_${namePanel}`}
          showCancelDownloadButton={showCancelButton}
          startDownloadProcess={startProcess}
          disableDownloadButtons={disableDownload || disabled}
          handleCancelDownload={() => handleCancelDownload(downloadType)}
          showDropdown={shouldShowDropdownForDownloadButton}
          onSwitchToBuy={() => setSelectedAction('buy')}
        />
      ) : (
        <DatasetActionButtons
          id={`${label}_${namePanel}`}
          data-testid={`${label}_${namePanel}`}
          addedToCart={addedToCart}
          addToCart={handleAddToCart}
          removeFromCart={handleRemoveFromCart}
          cartLoading={cartLoading}
          hasDatasets={hasDatasets}
          disabled={!isLocationAvailable || disabled}
          showDropdown={showDownloadButton}
          onSwitchToDownload={() => setSelectedAction('download')}
        />
      );
    }

    return (
      <DatasetActionButtons
        id={`${label}_${namePanel}`}
        data-testid={`${label}_${namePanel}`}
        addedToCart={addedToCart}
        addToCart={handleAddToCart}
        removeFromCart={handleRemoveFromCart}
        cartLoading={cartLoading}
        hasDatasets={hasDatasets}
        disabled={!isLocationAvailable || disabled}
        showDropdown={showDownloadButton}
      />
    );
  };

  const isSpectraButtonDisabled = () => {
    if (!show2DSpectra) return true;

    if (isAuthenticated) {
      return (
        disableDownload ||
        (!userGroupPermissions[userId || '']?.[namePanel]?.spectraAccess &&
          !showDownloadButton)
      );
    }

    return false;
  };

  const renderPrice = (price: number, isFree: boolean) => {
    if (priceLoading)
      return <Spin indicator={<LoadingOutlined spin />} size='small' />;

    return isFree ? 'Free' : PriceFormatter(price);
  };

  return (
    <Col span={12} className={styles.priceRegion}>
      <Row gutter={16} className={styles.rowHedder}>
        <Col span={8}>{isDTU && 'Time Step'}</Col>
        <Col span={5}>Price[EUR]</Col>
      </Row>

      {hasDatasets && isDTU && (
        <Row
          gutter={16}
          justify='space-between'
          align='middle'
          className={styles.rowBody}
        >
          <Col span={9} className={styles.colText}>
            <Select
              style={{ width: 80 }}
              onChange={(val) => setTimeStepInterval(val)}
              defaultValue='60'
              value={timeStepInterval}
              disabled={isEmpty(datasetId) || addedToCart}
              size='small'
              data-testid='PriceSection-Time_Step_Select'
            >
              <Select.Option value='10'>10 min</Select.Option>
              <Select.Option value='15'>15 min</Select.Option>
              <Select.Option value='30'>30 min</Select.Option>
              <Select.Option value='60'>1 hour</Select.Option>
            </Select>
          </Col>
          <Col span={5} className={styles.colPrice}>
            {renderPrice(price, price === 0 && isTnw)}
          </Col>
          <Col span={10} className={styles.buttonRow}>
            {renderActionButtons({
              showDownloadButton,
              showCancelButton: showCancelDownloadButtonParameters,
              downloadType: 'parameters',
              selectedAction: priceParameterSelection,
              setSelectedAction: setPriceParameterSelection,
            })}
          </Col>
        </Row>
      )}

      {hasDatasets && !isDTU && (
        <Row
          gutter={16}
          justify='space-between'
          align='middle'
          className={styles.rowBody}
        >
          <Col span={9} className={styles.colText}>
            Parameters
          </Col>
          <Col span={5} className={styles.colPrice}>
            {renderPrice(price, price === 0 && isTnw)}
          </Col>
          <Col span={9} className={styles.buttonRow}>
            {renderActionButtons({
              showDownloadButton,
              showCancelButton: showCancelDownloadButtonParameters,
              downloadType: 'parameters',
              selectedAction: priceParameterSelection,
              setSelectedAction: setPriceParameterSelection,
            })}
          </Col>
        </Row>
      )}

      {hasDatasets && namePanel === DownloadCheckboxKey.SW && (
        <Row
          gutter={16}
          justify='space-between'
          align='middle'
          className={styles.rowBody}
        >
          <Col
            span={9}
            className={show2DSpectra ? styles.colText : styles.disabledColText}
          >
            2D Spectra
          </Col>
          {show2DSpectra ? (
            <Col span={5} className={styles.colPrice}>
              {renderPrice(price, price === 0 && isTnw)}
            </Col>
          ) : (
            <Col span={5} className={styles.disabledColPrice}>
              -
            </Col>
          )}
          <Col span={9} className={styles.buttonRow}>
            {renderActionButtons({
              showDownloadButton: true,
              showCancelButton: showCancelDownloadButton2DSpectre,
              downloadType: '2DSpectra',
              disabled: isSpectraButtonDisabled(),
              authenticatedCheck: true,
              selectedAction: priceSpectraSelection,
              setSelectedAction: setPriceSpectraSelection,
            })}
          </Col>
        </Row>
      )}
    </Col>
  );
};
